import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { useCreateUserInvitation } from "../requests/userInvitations";
import { isValidEmail } from "../utils/isValidEmail";

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const createInvitation = useCreateUserInvitation();

  const handleSubmit = async () => {
    setError("");

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      await createInvitation.mutateAsync(email);
      setEmail("");
      onClose();
    } catch (err) {
      setError("Failed to send invitation. Please try again.");
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void handleSubmit();
            }
          }}
          error={!!error}
          helperText={error || "Enter the email address of the person you want to invite."}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createInvitation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createInvitation.isLoading || !email}
        >
          {createInvitation.isLoading ? <CircularProgress size={24} /> : "Send Invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
